import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { State } from 'types/redux'
import {
  changeMode,
  loadStats,
} from '@disco/state-management/actions/StatsActions'
import { ANALYTICS_UNLIMITED } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import StatsDateRange from 'components/common/stats-date-range'
import TrackName from 'components/common/track-name'
import DocumentHeadTags from 'components/document-head-tags'
import DataTable from 'components/ui/data-table'
import InfiniteScroll from 'components/ui/infinite-scroll'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

const InsightsTeamTracksPlaybacks = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    endDate,
    entities: { tracks },
    startDate,
  }: State['stats'] = useSelector(state => state.stats)
  const isFeatureEnabled = useFeatureEnabled(ANALYTICS_UNLIMITED)
  useEffect(() => {
    if (!isFeatureEnabled) {
      navigate('/insights/team')
    }
  }, [isFeatureEnabled, navigate])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const loadData = useCallback(async () => {
    await dispatch(loadStats('tracks', 'playbacks'))
    setIsLoading(false)
  }, [dispatch])
  useEffect(() => {
    dispatch(changeMode('team'))
  }, [])
  useEffect(() => {
    if (isFeatureEnabled) {
      setIsLoading(true)
      loadData()
    }
  }, [endDate, isFeatureEnabled, startDate])
  const loadNext = useCallback(
    (next: string) => {
      dispatch(loadStats('tracks', 'playbacks', true))
    },
    [dispatch],
  )
  return (
    <>
      <PageHeader.Breadcrumbs
        breadcrumbs={{
          content: 'Back',
          link: '/insights/team',
        }}
      />
      <PageContainer>
        <PageContainer.Section
          title="Most played tracks and videos"
          action={<StatsDateRange disabled={!isFeatureEnabled} />}
        >
          <DocumentHeadTags title="Most played tracks and videos | DISCO" />
          <InfiniteScroll next={tracks.playbacks.next} loadNext={loadNext}>
            <DataTable
              appearance="plain"
              columns={[
                {
                  name: 'File',
                  render: ({ stats, ...track }: Record<string, any>) => (
                    <TrackName track={track as any} />
                  ),
                },
                {
                  name: 'Artist',
                  render: (item: Record<string, any>) => item.artist,
                },
                {
                  name: 'Played',
                  width: 'content',
                  render: (item: Record<string, any>) => item.stats.play_count,
                },
              ]}
              data={tracks.playbacks.data}
              loading={isLoading}
              rows={5}
            />
          </InfiniteScroll>
        </PageContainer.Section>
      </PageContainer>
    </>
  )
}

export default InsightsTeamTracksPlaybacks
